import gql from 'graphql-tag';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getApolloCache } from '@confluence/graphql/entry-points/createClient.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { type FragmentReaderInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import {
	EDITOR_ONBOARDING_STATE_KEYS,
	ONBOARDING_TUTORIAL_LABEL,
} from '../../constants/onboarding-state/onboarding-editor-constants.ts';

import { getOnboardingStateGQL, setOnboardingStateGQL } from './OnboardingStatus.graphql.ts';
import { LabelsQuery } from './LabelsQuery.graphql.ts';
import type {
	OnboardingEditorLabelsQuery as OnboardingLabelsQueryType,
	OnboardingEditorLabelsQueryVariables,
} from './__types__/LabelsQuery.ts';
import type {
	GetOnboardingState,
	GetOnboardingStateVariables,
} from './__types__/getOnboardingStateGQL.ts';
import { deserializeState, getOnboardingStateFromCache } from './onboardingState.ts';

export const setHasUserPublished = () => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	void getApolloClient().mutate({
		mutation: setOnboardingStateGQL,
		variables: {
			key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
			value: 'true',
		},
		optimisticResponse: {
			onboardingState: [
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
					value: 'true',
					__typename: 'OnboardingState',
				},
			],
		},
		update(cache) {
			cache.writeFragment({
				id: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
				fragment: gql`
					fragment myKey on OnboardingState {
						key
						value
					}
				`,
				data: {
					key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
					value: 'true',
					__typename: 'OnboardingState',
				},
			});
		},
	});
};

const queryVariables = {
	key: Object.values(EDITOR_ONBOARDING_STATE_KEYS),
};

export const getEditorOnboardingQuery = (): void => {
	// When centralized onboarding state is enabled, skip this query as the centralized preloader handles it
	if (expValEquals('confluence_centralized_onboarding_state', 'isEnabled', true)) {
		return;
	}

	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	void getApolloClient().query({
		query: getOnboardingStateGQL,
		variables: queryVariables,
	});
};

export const getEditorOnboardingCache = (readEditorOnboardingState: FragmentReaderInterop<any>) => {
	try {
		let cachedEditorOnboardingData;
		const editorKeys = Object.values(EDITOR_ONBOARDING_STATE_KEYS);

		if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
			// each onboarding item is stored with the key as the cache id
			cachedEditorOnboardingData = editorKeys.map((id) =>
				readEditorOnboardingState({
					id,
				}),
			);
			// reformat to match query shape
			cachedEditorOnboardingData = { onboardingState: cachedEditorOnboardingData };
		} else if (expValEquals('confluence_centralized_onboarding_state', 'isEnabled', true)) {
			cachedEditorOnboardingData = getOnboardingStateFromCache(editorKeys);
		} else {
			cachedEditorOnboardingData = getApolloCache().readQuery<
				GetOnboardingState,
				GetOnboardingStateVariables
			>({
				query: getOnboardingStateGQL,
				variables: queryVariables,
			});
		}
		const state = deserializeState(cachedEditorOnboardingData);

		return state;
	} catch (cacheError) {
		// fire analytics
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'OnboardingState',
				actionSubject: 'getEditorOnboardingCache',
				action: 'failed',
				attributes: {
					error: cacheError,
				},
			});
		});

		// if cache is empty then call query to load cache
		void getEditorOnboardingQuery();

		// return default state on error
		return {};
	}
};

export const getLabelsQuery = (contentId: string): void => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	void getApolloClient().query<OnboardingLabelsQueryType, OnboardingEditorLabelsQueryVariables>({
		query: LabelsQuery,
		variables: {
			contentId,
			status: ['current', 'draft'],
		},
	});
};

export const getCachedIsOnboardingEditorLabel = (
	contentId: string,
	readLabelsFragment: FragmentReaderInterop<any>,
) => {
	try {
		let labels;
		if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
			labels =
				readLabelsFragment({
					id: `Content:${contentId}`,
				})?.nodes || [];
		} else {
			const cachedLabelsQueryData: OnboardingLabelsQueryType | null = getApolloCache().readQuery<
				OnboardingLabelsQueryType,
				OnboardingEditorLabelsQueryVariables
			>({
				query: LabelsQuery,
				variables: {
					contentId,
					status: ['current', 'draft'],
				},
			});

			labels = cachedLabelsQueryData?.content?.nodes?.[0]?.labels?.nodes || [];
		}

		// purposely exclude creator onboarding label as this is used for hiding the publish flag
		return (
			labels && Boolean(labels.find((label: any) => label?.label === ONBOARDING_TUTORIAL_LABEL))
		);
	} catch (cacheError) {
		// fire analytics
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'LabelsQuery',
				actionSubject: 'getEditorLabelsQueryCache',
				action: 'failed',
				attributes: {
					error: cacheError,
				},
			});
		});

		// if cache is empty then call query to load cache
		void getLabelsQuery(contentId);

		// return false on error
		return false;
	}
};
